import { useState, useMemo, useEffect } from "react";
import { usePagination } from "@/hooks/usePagination";
import { PaginationBar } from "@/components/PaginationBar";
import { Link } from "react-router-dom";
import { useAppData } from "@/data/AppDataContext";
import { useVendorPO } from "@/data/VendorPOContext";
import { useWarehouseInventory } from "@/data/WarehouseInventoryContext";
import { canEnterStage } from "@/data/workflowEngine";
import { getAllocationCoverage } from "@/data/inventory/allocationValidation";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { formatShortDate } from "@/lib/dateFormat";
import {
  Search, CheckCircle2, AlertTriangle, XCircle, Clock,
  Package, Truck, ClipboardCheck, CalendarDays, ExternalLink,
  ShieldCheck, Timer,
} from "lucide-react";
import { ReportFrictionButton } from "@/components/ReportFrictionButton";
import type { Job } from "@/data/types";
import { isUuid } from "@/lib/uuid";

/* ═══════════════════════════════════════════════════
   Types
   ═══════════════════════════════════════════════════ */

type ReadinessLane = "ready" | "almost" | "blocked" | "not-started";

interface Blocker {
  category: "review" | "ordering" | "receiving" | "allocation" | "issue";
  message: string;
  severity: "hard" | "soft";
}

interface EnrichedJob {
  job: Job;
  lane: ReadinessLane;
  blockers: Blocker[];
  materialPct: number;
  daysToReady: number | null;
  vendorStatuses: { vendor: string; ordered: boolean; received: boolean }[];
}

function isReceivingComplete(row: { status?: string; expectedQuantity?: number; receivedQuantity?: number }): boolean {
  const status = String(row.status || "").toLowerCase();
  if (status === "fully received" || status === "received") return true;
  const expected = Number(row.expectedQuantity || 0);
  const received = Number(row.receivedQuantity || 0);
  return Number.isFinite(expected) && expected > 0 && Number.isFinite(received) && received >= expected;
}

function isAllocationReadyForInstall(status?: string): boolean {
  const normalized = String(status || "").trim().toLowerCase();
  // Reserved means inventory is already committed to this job.
  return normalized === "ready for install" || normalized === "reserved";
}

/* ═══════════════════════════════════════════════════
   Helpers
   ═══════════════════════════════════════════════════ */

function getProductLabel(job: Job): string {
  const sanitize = (value: string): string => {
    const cleaned = value
      .split(/\s+/)
      .filter((token) => !isUuid(token))
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();
    return cleaned || "Unspecified Product";
  };
  const pc = job.projectConfig;
  if (!pc) return sanitize(job.projectType);
  const mat = pc.primaryMaterial || pc.selectedWallSystem || "";
  const sys = pc.bathroomSystemType || pc.autoProjectType || job.projectType;
  return sanitize(mat ? `${mat} ${sys}` : sys);
}

function getJobDisplayRef(job: Job): string {
  // Try jobNumber as string first
  if (typeof job.jobNumber === "string" && job.jobNumber.trim()) {
    return job.jobNumber.trim();
  }
  
  // Try to convert jobNumber to string if it's a number
  if (typeof job.jobNumber === "number" && !isNaN(job.jobNumber)) {
    return String(job.jobNumber);
  }
  
  // Never surface raw IDs/UUIDs in UI
  return "JOB-UNASSIGNED";
}

const LANE_CONFIG: Record<ReadinessLane, { label: string; color: string; bg: string; icon: typeof CheckCircle2 }> = {
  ready: { label: "Ready for Install", color: "text-success dark:text-success", bg: "border-l-emerald-500 bg-success/50 dark:bg-success/20", icon: CheckCircle2 },
  almost: { label: "Almost Ready", color: "text-warning dark:text-warning", bg: "border-l-amber-500 bg-warning/50 dark:bg-warning/20", icon: Clock },
  blocked: { label: "Blocked", color: "text-destructive", bg: "border-l-destructive bg-destructive/5", icon: XCircle },
  "not-started": { label: "Not Started", color: "text-muted-foreground", bg: "border-l-muted bg-muted/20", icon: Package },
};

/* ═══════════════════════════════════════════════════
   Component
   ═══════════════════════════════════════════════════ */

export default function InstallReadiness() {
  const { jobs, issues } = useAppData();
  const { getPOsByJob } = useVendorPO();
  const { receivingRecords, jobAllocations, isJobReadyForInstall, shipments } = useWarehouseInventory();

  const [search, setSearch] = useState("");
  const [activeLane, setActiveLane] = useState<ReadinessLane | "all">("all");
  const [detailJob, setDetailJob] = useState<EnrichedJob | null>(null);

  // Enrich all operational jobs
  const enrichedJobs = useMemo<EnrichedJob[]>(() => {
    // Only jobs that have closed a sale or are in operational stages
    const opJobs = jobs.filter(j =>
      j.currentStage !== "Completed" &&
      j.currentStage !== "Issue"
    );

    return opJobs.map(job => {
      const blockers: Blocker[] = [];
      const pos = getPOsByJob(job.id);
      const jobReceiving = receivingRecords.filter(r => r.jobId === job.id);
      const jobAllocs = jobAllocations.filter(a => a.jobId === job.id);
      const jobIssues = issues.filter(i => i.jobId === job.id && i.status !== "Resolved");
      const jobShipments = shipments.filter(s => pos.some(p => p.poId === s.poId));

      // ── Build Review check (via workflow engine) ──
      const reviewGate = canEnterStage(job, "Ready for Ordering");
      if (!reviewGate.met) {
        for (const blocker of reviewGate.blockers) {
          blockers.push({ category: "review", message: blocker.label, severity: "hard" });
        }
      }

      // ── Ordering check ──
      if (pos.length === 0 && !job.planningStatus?.includes("Ordered")) {
        if (job.approvedForOrdering || job.intakeApproved) {
          blockers.push({ category: "ordering", message: "No purchase orders created", severity: "hard" });
        }
      }

      // ── Per-vendor receiving (shipment status only) ──
      const vendorStatuses: { vendor: string; ordered: boolean; received: boolean }[] = [];
      const vendorGroups = new Map<string, { ordered: boolean; received: boolean }>();

        for (const po of pos) {
          const poReceiving = jobReceiving.filter(r => r.poId === po.poId);
          const poDelivered = po.status === "Delivered";
          const allReceived = poDelivered || (poReceiving.length > 0 && poReceiving.every(r => isReceivingComplete(r)));
          vendorGroups.set(po.vendorName, {
            ordered: true,
            received: allReceived,
          });
          if (!allReceived && poReceiving.length === 0) {
            blockers.push({
              category: "receiving",
              message: `Awaiting delivery from ${po.vendorName}`,
              severity: "hard",
            });
          }
        }
        vendorGroups.forEach((v, name) => vendorStatuses.push({ vendor: name, ...v }));

      // ── Allocation check (BOM vs job_allocations) ──
      const allocationCoverage = getAllocationCoverage(job, jobAllocations);
      if (allocationCoverage.length > 0) {
        for (const row of allocationCoverage) {
          if (row.isShort) {
            blockers.push({
              category: "allocation",
              message: `${row.productName}: allocated ${row.allocated} of ${row.required} required`,
              severity: "hard",
            });
          }
        }
      } else {
        // No BOM — fall back to checking if any allocations are not ready
        const notReady = jobAllocs.filter((a) => !isAllocationReadyForInstall(a.status));
        if (notReady.length > 0 && jobAllocs.length > 0) {
          blockers.push({
            category: "allocation",
            message: `${notReady.length} allocation(s) not marked ready`,
            severity: "soft",
          });
        }
      }

      // ── Issues check ──
      if (jobIssues.length > 0) {
        blockers.push({
          category: "issue",
          message: `${jobIssues.length} unresolved issue(s)`,
          severity: jobIssues.some(i => i.severity === "Urgent") ? "hard" : "soft",
        });
      }

      // ── Material completion % (allocation-based when BOM is available) ──
      let materialPct: number;
      if (allocationCoverage.length > 0) {
        const totalRequired = allocationCoverage.reduce((sum, r) => sum + r.required, 0);
        const totalAllocated = allocationCoverage.reduce((sum, r) => sum + Math.min(r.allocated, r.required), 0);
        materialPct = totalRequired > 0 ? Math.round((totalAllocated / totalRequired) * 100) : 0;
      } else {
        // No BOM — fall back to receiving-based progress
        const totals = jobReceiving.reduce(
          (acc, row) => {
            const expected = Number(row.expectedQuantity || 0);
            const received = Number(row.receivedQuantity || 0);
            if (Number.isFinite(expected) && expected > 0) {
              acc.expected += expected;
              acc.received += Math.max(0, Math.min(Number.isFinite(received) ? received : 0, expected));
            } else {
              acc.fallbackExpected += 1;
              if (isReceivingComplete(row)) acc.fallbackReceived += 1;
            }
            return acc;
          },
          { expected: 0, received: 0, fallbackExpected: 0, fallbackReceived: 0 },
        );
        materialPct = totals.expected > 0
          ? Math.round((totals.received / totals.expected) * 100)
          : totals.fallbackExpected > 0
            ? Math.round((totals.fallbackReceived / totals.fallbackExpected) * 100)
            : (pos.some((po) => po.status === "Delivered") ? 100 : 0);
      }

      // ── Days to ready prediction ──
      let daysToReady: number | null = null;
      if (blockers.length > 0) {
        const pendingShipments = jobShipments.filter(s => s.status !== "Received");
        if (pendingShipments.length > 0) {
          const latestEta = pendingShipments
            .filter(s => s.expectedDelivery)
            .map(s => new Date(s.expectedDelivery!).getTime())
            .sort((a, b) => b - a)[0];
          if (latestEta) {
            daysToReady = Math.max(0, Math.ceil((latestEta - Date.now()) / 86400000) + 1); // +1 for receiving
          }
        }
      }

      // ── Lane assignment ──
      let lane: ReadinessLane;
      const hardBlockers = blockers.filter(b => b.severity === "hard");
      if (blockers.length === 0 || isJobReadyForInstall(job.id)) {
        lane = "ready";
      } else if (hardBlockers.length === 0 && blockers.length > 0) {
        lane = "almost";
      } else if (!job.intakeApproved && !job.approvedForOrdering && pos.length === 0) {
        lane = "not-started";
      } else {
        lane = "blocked";
      }

      return { job, lane, blockers, materialPct, daysToReady, vendorStatuses };
    });
   }, [jobs, issues, getPOsByJob, receivingRecords, jobAllocations, shipments, isJobReadyForInstall]);

  // Filter
  const filtered = useMemo(() => {
    let result = enrichedJobs;
    if (activeLane !== "all") {
      result = result.filter(e => e.lane === activeLane);
    }
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(e =>
        e.job.customerName.toLowerCase().includes(s) ||
        e.job.id.toLowerCase().includes(s) ||
        getJobDisplayRef(e.job).toLowerCase().includes(s) ||
        getProductLabel(e.job).toLowerCase().includes(s)
      );
    }
    return result;
  }, [enrichedJobs, activeLane, search]);
  const pagination = usePagination(filtered, 20);
  useEffect(() => {
    pagination.reset();
  }, [search, activeLane]);

  // Counts
  const counts = useMemo(() => ({
    ready: enrichedJobs.filter(e => e.lane === "ready").length,
    almost: enrichedJobs.filter(e => e.lane === "almost").length,
    blocked: enrichedJobs.filter(e => e.lane === "blocked").length,
    notStarted: enrichedJobs.filter(e => e.lane === "not-started").length,
    total: enrichedJobs.length,
  }), [enrichedJobs]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <ShieldCheck className="h-6 w-6" /> Install Readiness
          </h1>
          <p className="text-sm text-muted-foreground">
            Real-time visibility into job readiness — Build Review, Ordering, Warehouse
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <ReportFrictionButton moduleName="Install Readiness" />
          <Badge className="bg-success/15 text-success dark:bg-success/30 dark:text-success text-xs">{counts.ready} Ready</Badge>
          <Badge className="bg-warning/15 text-warning dark:bg-warning/30 dark:text-warning text-xs">{counts.almost} Almost</Badge>
          <Badge variant="destructive" className="text-xs">{counts.blocked} Blocked</Badge>
        </div>
      </div>

      {/* KPI Lane Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {(["ready", "almost", "blocked", "not-started"] as ReadinessLane[]).map(lane => {
          const config = LANE_CONFIG[lane];
          const count = lane === "not-started" ? counts.notStarted : counts[lane as keyof typeof counts] as number;
          const Icon = config.icon;
          return (
            <Card
              key={lane}
              className={`cursor-pointer border-2 transition-colors ${activeLane === lane ? "border-primary" : "hover:border-primary/50"}`}
              onClick={() => setActiveLane(activeLane === lane ? "all" : lane)}
            >
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`p-2 rounded-lg ${config.bg} border-l-0`}>
                  <Icon className={`h-5 w-5 ${config.color}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{count}</p>
                  <p className="text-xs text-muted-foreground">{config.label}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Search + Filter */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search jobs..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Tabs value={activeLane} onValueChange={v => setActiveLane(v as typeof activeLane)} className="hidden md:block">
          <TabsList>
            <TabsTrigger value="all">All ({counts.total})</TabsTrigger>
            <TabsTrigger value="ready">Ready ({counts.ready})</TabsTrigger>
            <TabsTrigger value="almost">Almost ({counts.almost})</TabsTrigger>
            <TabsTrigger value="blocked">Blocked ({counts.blocked})</TabsTrigger>
            <TabsTrigger value="not-started">Not Started ({counts.notStarted})</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Job Cards */}
      {filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <ShieldCheck className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p className="font-medium">No jobs in this view</p>
            <p className="text-sm">Adjust filters or check other lanes</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {pagination.paged.map(ej => {
            const config = LANE_CONFIG[ej.lane];
            const Icon = config.icon;
            return (
              <Card
                key={ej.job.id}
                className={`border-l-4 cursor-pointer hover:shadow-md transition-shadow ${config.bg}`}
                onClick={() => setDetailJob(ej)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className={`h-4 w-4 shrink-0 ${config.color}`} />
                        <span className="font-semibold text-sm">{ej.job.customerName}</span>
                        <Badge variant="outline" className="text-[10px]">{getJobDisplayRef(ej.job)}</Badge>
                        {ej.daysToReady !== null && (
                          <Badge variant="secondary" className="text-[10px] gap-1">
                            <Timer className="h-3 w-3" /> ~{ej.daysToReady}d to ready
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mb-2">{getProductLabel(ej.job)}</p>

                      {/* Blockers */}
                      {ej.blockers.length > 0 && (
                        <div className="space-y-0.5">
                          {ej.blockers.slice(0, 3).map((b, i) => (
                            <p key={i} className={`text-[11px] flex items-center gap-1.5 ${b.severity === "hard" ? "text-destructive" : "text-warning dark:text-warning"}`}>
                              {b.severity === "hard" ? <XCircle className="h-3 w-3 shrink-0" /> : <AlertTriangle className="h-3 w-3 shrink-0" />}
                              {b.message}
                            </p>
                          ))}
                          {ej.blockers.length > 3 && (
                            <p className="text-[10px] text-muted-foreground ml-4.5">+{ej.blockers.length - 3} more</p>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="text-right shrink-0 space-y-1">
                      {ej.job.scheduledInstallDate && (
                        <p className="text-[10px] text-muted-foreground flex items-center gap-1 justify-end">
                          <CalendarDays className="h-3 w-3" /> {formatShortDate(ej.job.scheduledInstallDate)}
                        </p>
                      )}
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] text-muted-foreground">{ej.materialPct}%</span>
                        <Progress value={ej.materialPct} className="w-16 h-1.5" />
                      </div>
                      <Badge className={`text-[10px] ${
                        ej.lane === "ready" ? "bg-success/15 text-success dark:bg-success/30 dark:text-success" :
                        ej.lane === "almost" ? "bg-warning/15 text-warning dark:bg-warning/30 dark:text-warning" :
                        ej.lane === "blocked" ? "bg-destructive/10 text-destructive" :
                        "bg-muted text-muted-foreground"
                      }`}>
                        {config.label}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
      <PaginationBar {...pagination} />

      {/* ── Detail Dialog ── */}
      <Dialog open={!!detailJob} onOpenChange={(o) => !o && setDetailJob(null)}>
        {detailJob && (
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {(() => { const I = LANE_CONFIG[detailJob.lane].icon; return <I className={`h-5 w-5 ${LANE_CONFIG[detailJob.lane].color}`} />; })()}
                {detailJob.job.customerName}
                <Badge variant="outline" className="text-xs">{getJobDisplayRef(detailJob.job)}</Badge>
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              {/* Overview */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-muted/50 rounded-md p-3">
                  <p className="text-[10px] text-muted-foreground">Project</p>
                  <p className="text-sm font-medium">{getProductLabel(detailJob.job)}</p>
                </div>
                <div className="bg-muted/50 rounded-md p-3">
                  <p className="text-[10px] text-muted-foreground">Install Date</p>
                  <p className="text-sm font-medium">{formatShortDate(detailJob.job.scheduledInstallDate) || "Not scheduled"}</p>
                </div>
              </div>

              {/* Material Progress */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-medium">Material Completion</p>
                  <span className="text-xs font-bold">{detailJob.materialPct}%</span>
                </div>
                <Progress value={detailJob.materialPct} className="h-2" />
              </div>

              {/* Status Cards */}
              <div className="space-y-2">
                {/* Build Review */}
                <Card className="shadow-none">
                  <CardContent className="p-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ClipboardCheck className="h-4 w-4 text-muted-foreground" />
                      <span className="text-xs font-medium">Build Review</span>
                    </div>
                    {detailJob.job.intakeApproved || detailJob.job.approvedForOrdering ? (
                      <Badge className="text-[10px] bg-success/15 text-success dark:bg-success/30 dark:text-success">Approved</Badge>
                    ) : (
                      <Badge variant="destructive" className="text-[10px]">Not Approved</Badge>
                    )}
                  </CardContent>
                </Card>

                {/* Vendor Statuses */}
                {detailJob.vendorStatuses.length > 0 ? (
                  detailJob.vendorStatuses.map((vs, i) => (
                    <Card key={i} className="shadow-none">
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Truck className="h-4 w-4 text-muted-foreground" />
                            <span className="text-xs font-medium">{vs.vendor}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <Badge variant={vs.ordered ? "outline" : "secondary"} className="text-[10px]">
                              {vs.ordered ? "Ordered" : "Not Ordered"}
                            </Badge>
                            <Badge className={`text-[10px] ${vs.received ? "bg-success/15 text-success dark:bg-success/30 dark:text-success" : "bg-warning/15 text-warning dark:bg-warning/30 dark:text-warning"}`}>
                              {vs.received ? "Received" : "Pending"}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                ) : (
                  <Card className="shadow-none">
                    <CardContent className="p-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs font-medium">Ordering</span>
                      </div>
                      <Badge variant="secondary" className="text-[10px]">No Orders</Badge>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Blockers */}
              {detailJob.blockers.length > 0 && (
                <div>
                  <p className="text-xs font-bold mb-2 flex items-center gap-1">
                    <AlertTriangle className="h-3.5 w-3.5" /> Blockers ({detailJob.blockers.length})
                  </p>
                  <div className="space-y-1.5">
                    {detailJob.blockers.map((b, i) => (
                      <div key={i} className={`flex items-start gap-2 rounded-md p-2 ${b.severity === "hard" ? "bg-destructive/5" : "bg-warning/10 dark:bg-warning/20"}`}>
                        {b.severity === "hard"
                          ? <XCircle className="h-3.5 w-3.5 text-destructive mt-0.5 shrink-0" />
                          : <AlertTriangle className="h-3.5 w-3.5 text-warning mt-0.5 shrink-0" />
                        }
                        <div>
                          <p className={`text-xs ${b.severity === "hard" ? "text-destructive" : "text-warning dark:text-warning"}`}>{b.message}</p>
                          <p className="text-[10px] text-muted-foreground capitalize">{b.category}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Days to Ready */}
              {detailJob.daysToReady !== null && (
                <div className="bg-muted/50 rounded-md p-3 flex items-center gap-2">
                  <Timer className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs font-medium">Estimated ~{detailJob.daysToReady} day(s) to ready</p>
                    <p className="text-[10px] text-muted-foreground">Based on latest expected delivery dates</p>
                  </div>
                </div>
              )}

              {/* Scheduling Gate */}
              {detailJob.lane !== "ready" && (
                <div className="border border-destructive/30 rounded-md p-3 bg-destructive/5">
                  <p className="text-xs font-bold text-destructive flex items-center gap-1 mb-1">
                    <XCircle className="h-3.5 w-3.5" /> Scheduling Blocked
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    This job cannot be scheduled until all blockers are resolved. {detailJob.blockers.filter(b => b.severity === "hard").length} hard block(s) remaining.
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-2 pt-2 border-t">
                <Button variant="outline" size="sm" className="text-xs" asChild>
                  <Link to={`/jobs/${detailJob.job.id}`}>
                    <ExternalLink className="h-3 w-3 mr-1" /> View Job
                  </Link>
                </Button>
                {detailJob.lane === "ready" && (
                  <Button size="sm" className="text-xs" asChild>
                    <Link to="/scheduling">
                      <CalendarDays className="h-3 w-3 mr-1" /> Schedule Install
                    </Link>
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
